import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { AuthButton } from '../components/Button';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import {
  useDeletePlaceWorkMutation,
  useGetPlaceCodesLazyQuery,
  useGetPlaceWorksLazyQuery,
  useUpdatePlaceWorksDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import './PlaceWorkListPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';

type IPlaceWorkListPageProps = RouteComponentProps<{ placeId: string }> & {};

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 4;

/** 工種の編集に必要な権限の段階。編集・削除のボタン（AuthButton）と同じ段階にする */
const EDIT_AUTHORITY_LEVEL = 2;

/** 補修コードの施工管理に選ばれている工種の削除ボタンに出す、削除できない理由 */
const WORK_IN_USE_MESSAGE = '補修コードの施工管理で使用されているため削除できません';

function getDeleteTitle(inUse: boolean): string | undefined {
  if (inUse) {
    return WORK_IN_USE_MESSAGE;
  }
  return undefined;
}

export const PlaceWorkListPage: React.FC<IPlaceWorkListPageProps> = function (props) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [getPlaceWorks, getPlaceWorksResult] = useGetPlaceWorksLazyQuery({ variables: { placeId } });
  // サーバーは補修コードの施工管理に選ばれている工種を削除しないため、補修コードの一覧から使用中の工種を事前に調べる
  const [getPlaceCodes, getPlaceCodesResult] = useGetPlaceCodesLazyQuery({ variables: { placeId } });
  const [deletePlaceWork, deletePlaceWorkResult] = useDeletePlaceWorkMutation({
    onCompleted(result) {
      // 工種が使われていたりする場合は削除できずにfalseが返る
      if (result.deletePlaceWork) {
        toastContext.showToast('削除しました', 'success');
      } else {
        toastContext.showToast('補修コードの施工管理で使用されているため削除できませんでした', 'error');
      }

      // 削除を実行するたびにリストを更新する
      getPlaceWorks();
      getPlaceCodes();
    },
    onError() {
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  // 確認ダイアログを出すために削除対象を一旦状態に保存する
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: number, name: string }>();
  const [updateDisplayOrder] = useUpdatePlaceWorksDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getPlaceWorksResult.data?.getPlace.works ?? []).map((work) => work.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: false,
    save: (ids) => updateDisplayOrder({ variables: { placeId, workIds: ids } }),
  });

  if (!getPlaceWorksResult.called) {
    getPlaceWorks();
  }
  if (!getPlaceCodesResult.called) {
    getPlaceCodes();
  }

  if (getPlaceWorksResult.error) {
    return <GraphQLErrorDialog error={getPlaceWorksResult.error} />
  }
  if (getPlaceCodesResult.error) {
    return <GraphQLErrorDialog error={getPlaceCodesResult.error} />
  }

  if (
    getPlaceWorksResult.loading ||
    !getPlaceWorksResult.data ||
    getPlaceCodesResult.loading ||
    !getPlaceCodesResult.data
  ) {
    return <Loading />;
  }

  const works = getPlaceWorksResult.data.getPlace.works;
  const displayedWorks = orderByIds(works, reorderMode.displayedIds);
  const usedWorkIds = getPlaceCodesResult.data.getPlace.codes
    .map((code) => code.selected_work?.id)
    .filter((id): id is number => id !== undefined);

  let deleteDialog;
  if (deleteTarget) {
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="工種の削除"
        message={<p>「{deleteTarget.name}」を削除しますか？</p>}
        yesLabel="削除"
        onYes={() => {
          deletePlaceWork({ variables: { placeWorkId: deleteTarget.id } });
          setDeleteTarget(undefined);
        }}
        onNo={() => {
          setDeleteTarget(undefined);
        }}
      />
    );
  }

  let rows;
  if (displayedWorks.length === 0) {
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message="工種が登録されていません" />;
  } else {
    rows = displayedWorks.map((work, index) => {
      const inUse = usedWorkIds.indexOf(work.id) !== -1;
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(work.id, index)} />;
      }
      const actionCell = (
        <td className='App-table_button_cell'>
          <div className="App-table_actions">
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color='white'
              onClick={() => history.push(`/places/${placeId}/works/${work.id}/edit`)}
            >
              編集
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color='red'
              disabled={inUse}
              title={getDeleteTitle(inUse)}
              onClick={() => setDeleteTarget(work)}
            >
              削除
            </AuthButton>
          </div>
        </td>
      );

      // 「ID」は工種のデータの番号。補修分類・補修コードの一覧と同じく、並び替えても行といっしょに動き、値は変わらない
      return (
        <tr key={work.id} {...reorderMode.getRowProps(work.id)}>
          {handleCell}
          <td>{work.id}</td>
          <td>{work.name}</td>
          <td>
            <ul>
              {[work.default_step, ...work.steps].map((step, i) => (
                <li key={i}>{step.name}</li>
              ))}
            </ul>
          </td>
          {actionCell}
        </tr>
      );
    });
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <AuthButton
        authLv={EDIT_AUTHORITY_LEVEL}
        mini={true}
        onClick={() => history.push(`/places/${placeId}/works/new`)}
      >
        新規作成
      </AuthButton>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th>操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'placeWorks');

  return (
    <div className="PlaceWorkListPage-container">
      <Loading open={deletePlaceWorkResult.loading} />
      {deleteDialog}
      <SectionHeader title="工種" actions={headerActions} />
      <ListSummary reorder={reorderMode} shownCount={works.length} />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className='App-table'>
          <thead>
            <tr>
              {handleHeader}
              <th>ID</th>
              <th className='PlaceWorkListPage-work_name_cell'>工種</th>
              <th>工程</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
